<script setup lang="ts">
import {
  IDatabasePropertyResponseInstance,
} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IDatabasePropertyResponseInstance'
import DatabaseItemValueComponent
  from '@toolify/client/src/modules/databases/components/DatabaseItemValueComponent.vue'
import {PropertyStrategyThemeType} from '@toolify/client/src/modules/tableItems/enum/PropertyStrategyThemeType'
import {useValueTypeStrategy} from '../../../databases/strategies/PropertyValueTypeStrategy/composables/useValueTypeStrategy'
import FloatingTooltipComponent from '../../../modals/components/FloatingTooltipComponent.vue'
import MaskedItemValueStrategy
  from '@toolify/client/src/modules/databases/strategies/PropertyValueTypeStrategy/components/MaskedItemValueStrategy/MaskedItemValueStrategy.vue'
import {onMounted, ref, nextTick, computed} from 'vue'

interface Props {
  payload: {
    databaseProperty: IDatabasePropertyResponseInstance,
    currentFullValue: unknown,
    currentValue: unknown
    previousValue?: unknown
    previousFullValue?: unknown
    isValueRedacted?: boolean
  }
  createdAt: Date
}

const containerRef = ref<HTMLElement | null>(null)
const containerWidth = ref<number>(null)
const props = defineProps<Props>()

onMounted(async () => {
  await nextTick()
  if (containerRef.value) {
    containerWidth.value = containerRef.value.offsetWidth
  }
})

const property = computed(() => {
  return {
    ...props.payload.databaseProperty as IDatabasePropertyResponseInstance,
    width: containerWidth.value,
  }
})

const propertyTitle = computed(() => {
  return props.payload.databaseProperty?.title || ''
})

const maskedPayload = computed(() => ({
  stylesType: PropertyStrategyThemeType.Secondary,
  value: null,
  isEditable: false,
  property: property.value,
}))

const previousPayload = computed(() => {
  if (props.payload.previousFullValue === undefined || props.payload.previousFullValue === null) return null
  return {
    stylesType: PropertyStrategyThemeType.Secondary,
    value: props.payload.previousFullValue,
    property: property.value,
  }
})

const currentPayload = computed(() => {
  if (props.payload.currentFullValue === undefined || props.payload.currentFullValue === null) return null
  return {
    stylesType: PropertyStrategyThemeType.Secondary,
    value: props.payload.currentFullValue,
    property: property.value,
  }
})

const previousFullValueToText = computed(()=>{
  if (!previousPayload.value) return ''
  const valueTypeStrategy = useValueTypeStrategy(previousPayload.value.property.valueType)
  const fullValueToText = valueTypeStrategy?.fullValueToText
  if(!fullValueToText) {
    return ''
  }

  return fullValueToText(previousPayload.value.value, previousPayload.value.property)
})

const currentFullValueToText = computed(()=>{
  if (!currentPayload.value) return ''
  const valueTypeStrategy = useValueTypeStrategy(currentPayload.value.property.valueType)
  const fullValueToText = valueTypeStrategy?.fullValueToText
  if(!fullValueToText) {
    return ''
  }

  return fullValueToText(currentPayload.value.value, currentPayload.value.property)
})

const hasPreviousValue = computed(() => {
  return !!previousFullValueToText.value
})

const hasCurrentValue = computed(() => {
  return !!currentFullValueToText.value
})

</script>

<template>
  <div class="database-item-value-notification-item-content" ref="containerRef">
    <span class="property-title" v-if="propertyTitle">{{propertyTitle}}:</span>

    
    <template v-if="payload.isValueRedacted">
      <MaskedItemValueStrategy :payload="maskedPayload" :isRevealable="false"/>
    </template>

    <template v-else-if="hasPreviousValue && hasCurrentValue">
      <div class="value-wrapper previous-value-wrapper strikethrough">
        <FloatingTooltipComponent placement="top">
          <span class="previous-text-value">{{previousFullValueToText}}</span>
          <template #tooltip>{{previousFullValueToText}}</template>
        </FloatingTooltipComponent> 
      </div>
      <span class="separator">to</span>
      <div class="value-wrapper current-value-wrapper">
        <FloatingTooltipComponent placement="top">
          <div>
            <DatabaseItemValueComponent
              :valueType="payload.databaseProperty?.valueType"
              :valuePayload="currentPayload"
              :createdAt="createdAt"
            />
          </div>
          <template #tooltip>{{currentFullValueToText}}</template>
        </FloatingTooltipComponent> 
      </div>
    </template>

    <template v-else-if="!hasPreviousValue && hasCurrentValue">
      <div class="value-wrapper current-value-wrapper">
        <FloatingTooltipComponent placement="top">
          <div>
            <DatabaseItemValueComponent
              :valueType="payload.databaseProperty?.valueType"
              :valuePayload="currentPayload"
              :createdAt="createdAt"
            />
          </div>
          <template #tooltip>{{currentFullValueToText}}</template>
        </FloatingTooltipComponent> 
      </div>
    </template>

    <template v-else-if="hasPreviousValue && !hasCurrentValue">
      <div class="value-wrapper previous-value-wrapper strikethrough">
        <FloatingTooltipComponent placement="top">
          <span class="previous-text-value">{{previousFullValueToText}}</span>
          <template #tooltip>{{previousFullValueToText}}</template>
        </FloatingTooltipComponent> 
      </div>
      <span class="separator">to</span>
      <span class="empty-badge">{{$t('general.empty')}}</span>
    </template>
    
    <template v-else>
      <span class="empty-badge">{{$t('general.empty')}}</span>
    </template>
  </div>
</template>

<style scoped lang="scss">
@use '../../../../styles/variables' as variables;

.database-item-value-notification-item-content {
  display: flex;
  height: 100%;
  max-width: 100%;
  gap: map-get(variables.$spacing, space-1-5);
  align-items: center;
}
.database-item-value-notification-item-content *{
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis; 
  white-space: nowrap; 
  box-sizing: border-box; 
}

.property-title {
  color: map-get(variables.$colors, content-dark);
  text-transform: uppercase;
  font-size: map-get(variables.$textSizes, text-xs);
  flex-shrink: 0;
}

.separator {
  color: map-get(variables.$colors, content-dark);
  font-size: map-get(variables.$textSizes, text-base);
  flex-shrink: 0;
}

.empty-badge {
  color: map-get(variables.$colors, content-dark);
  font-size: map-get(variables.$textSizes, text-xs);
  font-style: italic;
  flex-shrink: 0;
}

.previous-value-wrapper {
  opacity: 0.5;
  display: flex;
  align-items: center;
}

.previous-text-value {
  color: map-get(variables.$colors, content-dark);
  text-decoration: line-through;
  font-size: map-get(variables.$textSizes, text-base);
}

.value-wrapper {
  position: relative;
  &:hover .copy-icon-wrapper {
     opacity: 1;
     visibility: visible;
  }
  &:deep(.status-item-value.type-secondary){
    margin-left: 0;
  }
}

.copy-icon-wrapper {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  opacity: 0;
}
</style>
